import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { TRY_ON_ACCEPTED_MIME_TYPES, TRY_ON_MAX_IMAGE_BYTES } from '../../services/api/tryOn';
import type { TryOnFraming } from '../../types/tryOn';
import { t } from '../../utils/ui-strings';
import { PhotoCropper } from './PhotoCropper';

interface ImageUploadStepProps {
  selectedFile: File | null;
  /** Tidigare uppladdad bild (sessionsminne) — visas när ingen ny fil valts. */
  existingImageUrl?: string | null;
  disabled?: boolean;
  framing?: TryOnFraming;
  onFileSelected: (file: File | null) => void;
  lang?: string;
}

/** Nycklar, inte text — språket avgörs vid rendering. */
const PHOTO_GUIDELINE_KEYS = [
  'try_on_tip_front',
  'try_on_tip_full_body',
  'try_on_tip_light',
  'try_on_tip_one_person',
  'try_on_tip_tight_clothes',
  'try_on_tip_background',
] as const;

const FEET_GUIDELINE_KEYS = [
  'try_on_feet_tip_angle',
  'try_on_feet_tip_floor',
  'try_on_feet_tip_visible',
  'try_on_tip_light',
] as const;

function formatMegabytes(bytes: number) {
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

export function ImageUploadStep({
  selectedFile,
  existingImageUrl,
  disabled = false,
  framing = 'full',
  onFileSelected,
  lang,
}: ImageUploadStepProps) {
  const [error, setError] = useState<string | null>(null);
  /** Den valda, ÄNNU OBESKURNA filen. Först efter beskärningen går den vidare. */
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const previewUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : null),
    [selectedFile],
  );

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    // Nollställs så att samma fil kan väljas igen efter en avbruten beskärning.
    event.target.value = '';
    setError(null);
    setPendingFile(null);

    if (!file) {
      onFileSelected(null);
      return;
    }

    if (!TRY_ON_ACCEPTED_MIME_TYPES.includes(file.type)) {
      setError(t('try_on_file_format', lang));
      onFileSelected(null);
      return;
    }

    if (file.size > TRY_ON_MAX_IMAGE_BYTES) {
      setError(
        t('try_on_file_too_large', lang).replace('{size}', formatMegabytes(TRY_ON_MAX_IMAGE_BYTES)),
      );
      onFileSelected(null);
      return;
    }

    // Format- och storlekskontrollen gäller den VALDA filen, före avkodning.
    // Efter beskärningen är den alltid en JPEG på några hundra kB.
    setPendingFile(file);
  };

  const displayedImage = previewUrl ?? existingImageUrl ?? null;

  if (pendingFile) {
    return (
      <PhotoCropper
        file={pendingFile}
        lang={lang}
        disabled={disabled}
        framing={framing}
        onCropped={(cropped) => {
          setPendingFile(null);
          onFileSelected(cropped);
        }}
        onCancel={() => setPendingFile(null)}
      />
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-brand-blue">
          {t(framing === 'feet' ? 'try_on_feet_your_photo' : 'try_on_your_photo', lang)}
        </h3>
        <p className="mt-1 text-sm text-text-grey">
          {t('try_on_photo_intro', lang)}
        </p>
      </div>

      <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {(framing === 'feet' ? FEET_GUIDELINE_KEYS : PHOTO_GUIDELINE_KEYS).map((ruleKey) => (
          <li
            key={ruleKey}
            className="flex items-center gap-2 rounded-lg bg-brand-beige-tint-1/40 px-3 py-2 text-xs text-brand-blue"
          >
            <span className="text-emerald-600">✓</span>
            {t(ruleKey, lang)}
          </li>
        ))}
      </ul>

      <label className="flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-brand-blue/30 bg-white/80 p-4 text-center transition hover:border-brand-blue">
        {displayedImage ? (
          <img
            src={displayedImage}
            alt={t('try_on_selected_photo', lang)}
            className="max-h-64 rounded-xl object-contain"
          />
        ) : (
          <div className="space-y-2">
            <div className="text-4xl">📷</div>
            <p className="font-medium text-brand-blue">{t('try_on_pick_image', lang)}</p>
            <p className="text-xs text-text-grey">
              JPG, PNG eller WebP · max {formatMegabytes(TRY_ON_MAX_IMAGE_BYTES)}
            </p>
          </div>
        )}
        <input
          className="sr-only"
          type="file"
          accept={TRY_ON_ACCEPTED_MIME_TYPES.join(',')}
          disabled={disabled}
          onChange={handleChange}
        />
      </label>

      {displayedImage && !selectedFile ? (
        <p className="text-xs text-text-grey">
          {t('try_on_reuse_photo', lang)}
        </p>
      ) : null}

      {error ? <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p> : null}
      {selectedFile ? (
        <p className="text-xs text-text-grey">
          {t('try_on_selected_file', lang)}{' '}
          <span className="font-medium text-brand-blue">{selectedFile.name}</span>
        </p>
      ) : null}
    </section>
  );
}
